import { useSyncExternalStore } from 'react';

import { OPTIMISTIC_AGENT_REPLY_ID_PREFIX } from './agentMentionConstants.ts';
import {
	findPostedCommentAgentReply,
	type PostedReplyMatch,
	type ReplyMatchComment,
} from './hasPostedCommentAgentReply.ts';

// Retain matching information across approval unmounts and delayed processed-cache updates.
// Active submissions are never evicted. Settled records survive briefly to cover
// delayed cache snapshots, then expire; they contain no comment bodies.
const pendingReplies = new Map<
	string,
	{ match?: PostedReplyMatch; savedId?: string; rendered?: boolean }
>();
const settledReplies = new Map<string, ReturnType<typeof setTimeout>>();
const SETTLED_REPLY_RETENTION_MS = 60_000;
const MAX_SETTLED_REPLIES = 100;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};
const notify = () => listeners.forEach((listener) => listener());
// One per preview still in the comments cache; each re-adds its preview if a server write dropped it.
const restorers = new Set<() => void>();

export function clearOptimisticAgentRepliesForTesting(): void {
	settledReplies.forEach(clearTimeout);
	settledReplies.clear();
	pendingReplies.clear();
	listeners.clear();
	restorers.clear();
}

export function registerOptimisticAgentReplyRestorer(restore: () => void): () => void {
	restorers.add(restore);
	return () => {
		restorers.delete(restore);
	};
}

export function restoreOptimisticAgentReplies(): void {
	restorers.forEach((restore) => restore());
}

export function useOptimisticAgentReplyRendered(id: string): boolean {
	return useSyncExternalStore(
		subscribe,
		() => pendingReplies.get(id)?.rendered ?? false,
		() => false,
	);
}

// Called in the reply list's layout effect, so the preview disappears before
// paint only once its replacement is mounted. Keep the acknowledgement after
// cache cleanup so a delayed server response cannot bring the preview back.
export function markAgentRepliesRendered(ids: readonly string[]): void {
	const visible = new Set(ids);
	let changed = false;
	for (const [id, pending] of pendingReplies) {
		if (
			!pending.rendered &&
			(visible.has(id) || (pending.savedId && visible.has(pending.savedId)))
		) {
			pending.rendered = true;
			changed = true;
		}
	}
	if (changed) notify();
}

export function registerOptimisticAgentReply(id: string, match: PostedReplyMatch): void {
	clearTimeout(settledReplies.get(id));
	settledReplies.delete(id);
	pendingReplies.set(id, { match });
	notify();
}

function forgetSettledReply(id: string): void {
	clearTimeout(settledReplies.get(id));
	settledReplies.delete(id);
	pendingReplies.delete(id);
	notify();
}

/** Called after publication/rollback, not when the approval view unmounts. */
export function settleOptimisticAgentReply(id: string): void {
	if (!pendingReplies.has(id) || settledReplies.has(id)) return;
	settledReplies.set(
		id,
		setTimeout(() => forgetSettledReply(id), SETTLED_REPLY_RETENTION_MS),
	);
	if (settledReplies.size > MAX_SETTLED_REPLIES) {
		const oldest = settledReplies.keys().next().value;
		if (oldest) forgetSettledReply(oldest);
	}
}

// Pure calculation: interrupted or discarded React renders cannot claim a saved reply.
function findReplyPairings(replies: ReplyMatchComment[]): Map<string, string> {
	const optimistic = replies.filter((reply) =>
		reply.id.startsWith(OPTIMISTIC_AGENT_REPLY_ID_PREFIX),
	);
	const pairings = new Map<string, string>();
	if (!optimistic.length) return pairings;
	const comments = Object.fromEntries(replies.map((reply) => [reply.id, reply]));
	const claimed = new Set(
		Array.from(pendingReplies.values()).flatMap(({ savedId }) => (savedId ? [savedId] : [])),
	);
	for (const reply of optimistic) {
		const pending = pendingReplies.get(reply.id);
		if (!pending) continue;
		if (pending.savedId) {
			if (comments[pending.savedId]) pairings.set(reply.id, pending.savedId);
			continue;
		}
		const { match } = pending;
		if (!match) continue;
		const saved = findPostedCommentAgentReply(comments, {
			...match,
			existingCommentIds: new Set([...match.existingCommentIds, ...claimed]),
		});
		if (saved) {
			pairings.set(reply.id, saved.id);
			claimed.add(saved.id);
		}
	}
	return pairings;
}

/** Select one row without changing shared state during render. */
export function reconcileOptimisticAgentReplies<T extends ReplyMatchComment>(replies: T[]): T[] {
	const pairings = findReplyPairings(replies);
	return pairings.size ? replies.filter((reply) => !pairings.has(reply.id)) : replies;
}

/** Persist pairings only from the committed reply list's layout effect. */
export function commitOptimisticAgentReplyMatches(replies: ReplyMatchComment[]): void {
	for (const [id, savedId] of findReplyPairings(replies)) {
		const pending = pendingReplies.get(id);
		if (pending) {
			pending.savedId = savedId;
			// The saved ID is sufficient from now on; release the potentially large
			// snapshot of all comment IDs captured when the action was confirmed.
			pending.match = undefined;
		}
	}
}
